import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'

import { detectExtension } from '../utils/extensionBridge'

const POLL_INTERVAL_MS = 4000

const ExtensionStatusContext = createContext({
	status: null,
	loading: true,
	installed: false,
	compatible: false,
	recheck: () => {},
})

// Single source of truth for whether the LoadLine Chrome extension is
// installed and compatible. One probe loop runs at the provider level;
// every consumer reads from the same status so we don't spawn a new
// detection loop per component.
export function ExtensionStatusProvider({ children }) {
	const [status, setStatus] = useState(null)
	const [loading, setLoading] = useState(true)
	const mountedRef = useRef(true)
	const inFlightRef = useRef(false)

	const probe = useCallback(async () => {
		if (inFlightRef.current) return
		inFlightRef.current = true
		try {
			const result = await detectExtension()
			if (mountedRef.current) {
				setStatus(result)
				setLoading(false)
			}
		} catch {
			if (mountedRef.current) {
				setStatus({
					installed: false,
					compatible: false,
					action: 'install',
					message: 'LoadLine extension is not installed.',
				})
				setLoading(false)
			}
		} finally {
			inFlightRef.current = false
		}
	}, [])

	useEffect(() => {
		mountedRef.current = true
		probe()
		return () => {
			mountedRef.current = false
		}
	}, [probe])

	useEffect(() => {
		if (status?.compatible) return
		const id = setInterval(probe, POLL_INTERVAL_MS)
		return () => clearInterval(id)
	}, [status?.compatible, probe])

	useEffect(() => {
		if (status?.compatible) return
		const onFocus = () => probe()
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [status?.compatible, probe])

	const value = {
		status,
		loading,
		installed: Boolean(status?.installed),
		compatible: Boolean(status?.compatible),
		recheck: probe,
	}

	return (
		<ExtensionStatusContext.Provider value={value}>
			{children}
		</ExtensionStatusContext.Provider>
	)
}

export function useExtensionStatus() {
	return useContext(ExtensionStatusContext)
}
